import { createPaginator } from '@orange-marmalade/paginate-this';
import { fromJS, Record } from 'immutable';
import { createDuck } from 'redux-duck';
import moment from 'moment';

// middleware
import PaginateThisDuckCreator from '../middleware/paginate-this';
import { api, cleanQuery, formatError } from '../middleware/client';

// ducks
import { Photo as PhotoRecord, Agent as AgentRecord } from '../records';

const BASE_URL = `${process.env.REACT_APP_REST_URI}/properties`;
const locator = ({ PropertySearch }) => PropertySearch.get('list');
const listId = 'properties';
const defaultQuery = {
  page: 1,
  pageSize: 20,
  sort: 'createdDate',
  sortReverse: true,
  view: 'thumb',
  propertyClass: 'residential'
};
const sortFields = [
  {
    id: 'currentPrice',
    name: 'Price: High to Low',
    order: 'DESC',
    propertyClass: ['residential', 'residential_income', 'rental', 'commercial', 'land']
  },
  {
    id: 'currentPrice',
    name: 'Price: Low to High',
    order: 'ASC',
    propertyClass: ['residential', 'residential_income', 'rental', 'commercial', 'land']
  },
  {
    id: 'bldgDesign',
    name: 'Building Design: A-Z',
    order: 'DESC',
    propertyClass: ['residential_income']
  },
  {
    id: 'bldgDesign',
    name: 'Building Design: Z-A',
    order: 'ASC',
    propertyClass: ['residential_income']
  },
  {
    id: 'grossRentalIncome',
    name: 'Rental Income: High to Low',
    order: 'DESC',
    propertyClass: ['residential_income']
  },
  {
    id: 'grossRentalIncome',
    name: 'Rental Income: Low to High',
    order: 'ASC',
    propertyClass: ['residential_income']
  },
  {
    id: 'livingArea',
    name: 'Living Area: High to Low',
    order: 'DESC',
    propertyClass: ['residential', 'rental']
  },
  {
    id: 'livingArea',
    name: 'Living Area: Low to High',
    order: 'ASC',
    propertyClass: ['residential', 'rental']
  },
  {
    id: 'acres',
    name: 'Acres: High to Low',
    order: 'DESC',
    propertyClass: ['rental']
  },
  {
    id: 'acres',
    name: 'Acres: Low to High',
    order: 'ASC',
    propertyClass: ['rental']
  },
  {
    id: 'developmentName',
    name: 'Community Name: A-Z',
    order: 'ASC',
    propertyClass: ['residential', 'rental', 'land']
  },
  {
    id: 'developmentName',
    name: 'Community Name: Z-A',
    order: 'DESC',
    propertyClass: ['residential', 'rental', 'land']
  },
  {
    id: 'createdDate',
    name: 'Newest First',
    order: 'DESC',
    propertyClass: ['residential', 'residential_income', 'rental', 'commercial', 'land']
  }
];

export class PropertyRecord extends Record({
  id: undefined,
  displayAgent: new AgentRecord(),
  listingAgent: undefined,
  coListingAgent: undefined,
  sellingAgent: undefined,
  coSellingAgent: undefined,
  photos: [],
  latitude: undefined,
  longitude: undefined,
  currentPrice: null,
  bldgDesign: '',
  yearBuilt: null,
  livingArea: null,
  bedrooms: null,
  bathsFull: null,
  bathsHalf: null,
  mlsNumber: null,
  condoName: '',
  development: '',
  developmentName: '',
  status: undefined,
  propertyInfo: '',
  propertyType: '',
  unitFloors: null,
  totalFloors: null,
  rearExposure: '',
  privatePool: false,
  privateSpa: false,
  garages: null,
  furnished: '',
  pets: '',
  elevator: '',
  communityType: '',
  security: '',
  view: '',
  boatAccess: '',
  shortSale: false,
  foreclosed: false,
  waterfront: 'None',
  fullAddress: '',
  city: '',
  stateOrProvince: '',
  postalCode: '',
  amenities: 'None',
  interior: 'None',
  equipment: 'None',
  exterior: 'None',
  propertyClass: 'residential',
  listAgentFullName: '',
  listAgentMlsId: null,
  listOfficeMLSID: null,
  listOfficeName: '',
  photoPath: '',
  isSold: false,
  closingPrice: null,
  closingDate: undefined,
  // res / rentals / commerical
  buyerAgentComp: '',
  transBrokerComp: '',
  nonRepComp: '',
  // res
  parking: '',
  garageDesc: '',
  openhouses: [],
  // garages: '',
  elementarySchool: '',
  middleSchool: '',
  highSchool: '',
  taxDesc: '',
  taxDistrictType: '',
  // taxes: '',
  taxYear: '',
  hoaDesc: '',
  hoaFee: '',
  hoaFreq: '',
  masterHOAFee: '',
  masterHOAFreq: '',
  condoFee: '',
  condoFreq: '',
  clubFee: '',
  clubFeeFreq: '',

  // rentals
  monthlyRate: null,
  seasonRate: null,
  offSeasonRate: null,
  weeklyRate: null,
  rnTerm: null,
  monthsAvailable: null,
  weeksAvailable: null,

  // commercial
  transactionType: '',
  subjectPropertySqFt: null,
  commercialType: '',

  // land
  pricePerAcre: null,
  acres: null,
  lotFrontage: null,
  lotLeft: null,
  lotBack: null,
  lotRight: null,
  lot: '',
  lotType: '',
  countyOrParish: '',
  section: null,
  township: null,
  range: null,
  block: null,
  lotUnit: null,
  legalUnit: null,
  elevation: 'None',
  taxes: null,
  usage: null,
  legalDesc: 'None',
  zoningCode: 'None',
  utilitiesAvailable: '',
  groundCover: 'None',
  sewer: 'None',
  landImprovements: 'None',
  availabilityType: undefined
}) {
  constructor(data = {}) {
    if (data.openhouses) {
      data.openhouses = data.openhouses.map(openhouse => {
        // openhouse parsing fails the second time
        if (openhouse.startDate || openhouse.endDate) {
          return openhouse;
        }
        const date = openhouse.openHouseDate;
        const startHour = Math.floor(openhouse.startTime / 100);
        const endHour = Math.floor(openhouse.endTime / 100);

        return {
          startDate: moment(`${date} ${startHour}`, 'YYYY-MM-DD k').format(),
          endDate:
            endHour > 0
              ? moment(`${date} ${endHour}`, 'YYYY-MM-DD k').format()
              : undefined
        };
      });
    }

    if (data.photos) {
      data.photos = data.photos.map(photo => new PhotoRecord(photo));
    }

    if (data.listingAgent) {
      data.listingAgent = new AgentRecord({
        type: 'listingAgent',
        ...data.listingAgent
      });
    }

    if (data.coListingAgent) {
      data.coListingAgent = new AgentRecord({
        type: 'coListingAgent',
        ...data.coListingAgent
      });
    }

    if (data.sellingAgent) {
      data.sellingAgent = new AgentRecord({
        type: 'sellingAgent',
        ...data.sellingAgent
      });
    }

    if (data.coSellingAgent) {
      data.coSellingAgent = new AgentRecord({
        type: 'coSellingAgent',
        ...data.coSellingAgent
      });
    }

    data.displayAgent = data.displayAgent
      ? new AgentRecord(data.displayAgent)
      : data.listingAgent ||
        data.coListingAgent ||
        data.sellingAgent ||
        data.coSellingAgent ||
        undefined;

    return super(data);
  }
}

const paginateReducer = createPaginator({
  listId,
  fetch,
  locator,
  cache: true,
  initialSettings: {
    // initialized is true to prevent extra actions being fired
    // since we actually initialize the list using query parameters
    initialized: true,
    ...defaultQuery
  }
});

const duck = createDuck('PropertySearch', 'nabor');
const LOAD_TYPE = duck.defineType('LOAD');
const LOAD_SUCCESS_TYPE = duck.defineType('LOAD_SUCCESS');
const LOAD_FAIL_TYPE = duck.defineType('LOAD_FAIL');
const ABORTED_REQUEST_TYPE = duck.defineType('ABORTED_REQUEST');
const ABORTED_REQUEST = duck.createAction(ABORTED_REQUEST_TYPE);

// custom abort support
var lastRequest;

const initialState = fromJS({
  list: paginateReducer(),
  detail: {
    loaded: false,
    loading: false,
    record: new PropertyRecord()
  }
});

function reducer(state = initialState, action) {
  if (action.type === LOAD_TYPE) {
    state = state.mergeIn(['detail'], {
      loaded: action.id,
      loading: true,
      error: null,
      record: new PropertyRecord()
    });
  } else if (action.type === LOAD_SUCCESS_TYPE) {
    state = state.mergeIn(['detail'], {
      loaded: action.id,
      loading: false,
      record: new PropertyRecord(action.payload)
    });
  } else if (action.type === LOAD_FAIL_TYPE) {
    state = state.mergeIn(['detail'], {
      loading: false,
      error: formatError(action.error)
    });
  } else if (action.type === ABORTED_REQUEST_TYPE) {
    state = state.setIn(['list', 'isLoading'], false);
  } else {
    state = state.setIn(['list'], paginateReducer.call(this, state.get('list'), action));
  }
  return state;
}

function abortRequest() {
  if (lastRequest && lastRequest.abort) {
    lastRequest.abort();
  }

  return ABORTED_REQUEST(lastRequest);
}

function fetch(params) {
  const { id, query, propertyClass } = params;

  if (id) {
    let url = `${BASE_URL}/${id}`;
    return {
      types: [LOAD_TYPE, LOAD_SUCCESS_TYPE, LOAD_FAIL_TYPE],
      promise: client =>
        client
          .get(url, {
            query: cleanQuery({
              propertyClass
            })
          })
          .then(function (response) {
            response.amenities = response.amenities || '';
            response.boatAccess = response.boatAccess || '';
            response.utilitiesAvailable = response.utilitiesAvailable || '';
            response.waterfront = response.waterfront || '';
            response.landImprovements = response.landImprovements || '';

            return new PropertyRecord(response).toJS();
          }),
      id
    };
  } else {
    return (dispatch, getState) => {
      const state = getState();
      const countOnly = state.form['property-search-form'].values.countOnly;
      const isAdvancedResults =
        state.routing.location.pathname === '/find-naples-home/advanced/results';
      lastRequest = api.get(BASE_URL, {
        query: cleanQuery({
          ...query,
          page: undefined,
          pageSize: undefined,
          offset: query.pageSize * (query.page - 1),
          limit: query.pageSize,
          countOnly: !isAdvancedResults ? countOnly : 0
        })
      });

      return lastRequest
        .then(function (response) {
          lastRequest = undefined;
          return {
            data: {
              results: response.list.map(result => new PropertyRecord(result).toJS()),
              total_count: response.total
            }
          };
        })
        .catch(error => {
          throw formatError(error);
        });
    };
  }
}

const parseNumber = v => {
  v = parseInt(v, 10);

  return isNaN(v) ? '' : v;
};

// const parseBoolean = v => {
//   return v !== undefined && v !== null && (v === true || v === 1 || v == 1);
// };

const paginateThis = new PaginateThisDuckCreator({
  listId,
  locator,
  defaultQuery,
  abortRequest,
  // Lists filters that will automatically get parsed from the query string
  availableFilters: {
    mlsNumber: undefined,
    address: undefined,
    minPrice: parseNumber,
    maxPrice: parseNumber,
    propertyClass: undefined,
    propertyType: undefined,
    minBed: parseNumber,
    maxBed: parseNumber,
    numBaths: parseNumber,
    minArea: parseNumber,
    maxArea: parseNumber,
    buildingType: undefined,
    amenities: undefined,
    communityType: undefined,
    minYear: parseNumber,
    maxYear: parseNumber,
    pets: undefined,
    waterfront: undefined,
    shortSale: parseNumber,
    foreclosed: parseNumber,
    city: undefined,
    view: undefined,
    agentId: undefined,
    waterfrontYN: parseNumber,
    recentlySold: parseNumber,
    status: undefined,
    minFloors: parseNumber,
    maxFloors: parseNumber,
    privatePool: parseNumber,
    minGarageSpaces: parseNumber,
    maxGarageSpaces: parseNumber,
    furnished: undefined,
    ohStartDate: undefined,
    ohEndDate: undefined,
    // residential_income
    minGrossIncome: parseNumber,
    maxGrossIncome: parseNumber,
    // commercial
    transactionType: undefined,
    commercialType: undefined,
    minLandArea: parseNumber,
    maxLandArea: parseNumber,
    // rental
    availabilityType: undefined,
    rnTerm: undefined,
    // custom
    pointOfInterestId: parseNumber,
    communityId: parseNumber,
    communityName: undefined,
    golfCourseId: parseNumber,
    firm: undefined,
    firmId: undefined
  }
});

const PROPERTY_TYPE_MAP = {
  residential: 'Residential',
  rental: 'Rental',
  residential_income: 'Residential Income',
  land: 'Lot & Land',
  commercial: 'Commercial'
};

export { paginateThis, fetch, sortFields, PROPERTY_TYPE_MAP };
export default reducer;



// WEBPACK FOOTER //
// ./src/ducks/PropertySearch.js